import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import ManagerSidebar from '../manager-dashboard/components/ManagerSidebar';
import ManagerBottomNav from '../manager-dashboard/components/ManagerBottomNav';
import ManagerMobileHeader from '../manager-dashboard/components/ManagerMobileHeader';
import { calculateManagerBonus } from '../../utils/bonusCalculator';
import OpportunitesBonusSection from '../performance-actuelle/components/OpportunitesBonusSection';
import ActivenessDistributionCard from '../../components/ActivenessDistributionCard';


// Manager Performance page — wraps admin performance with manager context
// The actual filtering is handled by passing managerTiktokIds context
const ManagerPerformance = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [managerProfile, setManagerProfile] = useState(null);
  const [managerTiktokIds, setManagerTiktokIds] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      // Step 1: get auth user
      const { data: { user: authUser } } = await supabase?.auth?.getUser();
      if (!authUser) { navigate('/login-authentication'); return; }
      setAuthUser(authUser);

      // Step 2: get manager profile
      const { data: profile } = await supabase?.from('users')?.select('*')?.eq('id', authUser?.id)?.single();
      if (!profile) { navigate('/login-authentication'); return; }
      setManagerProfile(profile);

      const { data: creators } = await supabase?.from('creators')?.select('tiktok_id')?.eq('manager_id', authUser?.id);
      setManagerTiktokIds(creators?.map(c => c?.tiktok_id) || []);
      setLoading(false);
    };
    init();
  }, []);

  if (loading) {
    return (
      <div className="flex h-[100dvh] items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Render the full performance page with manager sidebar overlay
  // We use a custom layout that replaces the admin sidebar with manager sidebar
  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: '#0a0a0a' }}>
      <ManagerSidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(v => !v)}
        managerName={managerProfile?.name}
      />
      <div className="flex-1 overflow-y-auto pb-16 md:pb-0">
        <ManagerMobileHeader title={t('performance.title')} />
        <ManagerPerformanceContent
          managerProfile={managerProfile}
          authUser={authUser}
        />
      </div>
      <ManagerBottomNav />
    </div>
  );
};

// ─── Manager Performance Content (inline, filtered) ───────────────────────────
import { useMemo } from 'react';

const SERVER_CONFIG = {
  MENA: { color: '#f97316', fallbackTarget: 17832000 },
  FR:   { color: '#3b82f6', fallbackTarget: 6642993  },
  IT:   { color: '#22c55e', fallbackTarget: 3491528  },
  DE:   { color: '#a855f7', fallbackTarget: 752254   },
};

const SERVERS = ['Tous', 'MENA', 'FR', 'IT', 'DE'];

const fmtM = (n) => {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000)?.toFixed(2) + 'M';
  if (n >= 1_000) return (n / 1_000)?.toFixed(1) + 'K';
  return String(Math.round(n));
};

const fmtUSD = (n) => { if (!n) return '$0'; return '$' + Math.round(n)?.toLocaleString('fr-FR'); };

const ServerBadge = ({ server }) => {
  const cfg = SERVER_CONFIG?.[server];
  if (!cfg) return null;
  return (
    <span className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-md" style={{ backgroundColor: `${cfg?.color}22`, color: cfg?.color, fontFamily: 'Sora, sans-serif' }}>
      {server}
    </span>
  );
};

const getProgressColor = (pct) => pct >= 90 ? '#22c55e' : pct >= 70 ? '#f97316' : '#ef4444';

const ManagerPerformanceContent = ({ managerProfile, authUser }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedServer, setSelectedServer] = useState('Tous');
  const [levelFilter, setLevelFilter] = useState('');
  const [search, setSearch] = useState('');
  const [allData, setAllData] = useState([]);
  const [completionTiersMap, setCompletionTiersMap] = useState({});
  const [activenessConfig, setActivenessConfig] = useState([]);
  const [rankupConfig, setRankupConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [incrementalRate, setIncrementalRate] = useState(50);
  const [monthlyBonusMap, setMonthlyBonusMap] = useState({});

  // Table state
  const [sortKey, setSortKey] = useState('total_diamonds');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const now = new Date();
  const year = now?.getFullYear();
  const month = now?.getMonth();
  const currentPeriod = `${year}-${String(month + 1)?.padStart(2, '0')}`;

  useEffect(() => {
    if (!authUser) return;
    fetchData();
  }, [authUser]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch creator_current_month by manager_id
      const { data: currentData } = await supabase?.from('creator_current_month')?.select('*')?.eq('manager_id', authUser?.id);

      const CURRENT_PERIOD = currentData?.[0]?.period ||
        `${new Date()?.getFullYear()}-${String(new Date()?.getMonth()+1)?.padStart(2,'0')}`;

      setAllData(currentData || []);

      // Fetch config
      const { data: configData } = await supabase?.from('incremental_config')?.select('server, completion_tiers')?.eq('period', CURRENT_PERIOD);
      const tiersMap = {};
      configData?.forEach(r => { tiersMap[r.server] = r?.completion_tiers; });
      setCompletionTiersMap(tiersMap);

      const { data: actvCfg } = await supabase?.from('activeness_config')?.select('*');
      setActivenessConfig(actvCfg || []);

      const { data: rkCfg } = await supabase?.from('rankup_config')?.select('*');
      setRankupConfig(rkCfg || []);

      // Fetch incremental_rate from manager_rates for current period
      const { data: rateData } = await supabase?.from('manager_rates')?.select('incremental_rate')?.eq('manager_id', authUser?.id)?.eq('period', CURRENT_PERIOD)?.single();
      setIncrementalRate(rateData?.incremental_rate || 50);

      // Fetch creator_monthly_history for bonus/charge/incremental_bonus
      const { data: monthlyRows } = await supabase?.from('creator_monthly_history')?.select('tiktok_id, total_bonus, charge, incremental_bonus')?.eq('manager_id', authUser?.id)?.eq('period', CURRENT_PERIOD);
      const mMap = {};
      monthlyRows?.forEach(r => { mMap[r.tiktok_id] = r; });
      setMonthlyBonusMap(mMap);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Build creator aggregates from creator_current_month
  const creatorAggregates = useMemo(() => {
    return (allData || [])?.map(row => ({
      tiktok_id: row?.tiktok_id,
      username: row?.username,
      server: row?.server,
      level: row?.level,
      group_name: row?.group_name,
      total_diamonds: row?.diamonds || 0,
      live_days: row?.live_days || 0,
      live_duration_minutes: row?.live_duration_minutes || 0,
      tier_m1: row?.tier_m1,
      tier_current: row?.tier_current,
      bonus_rankup: row?.bonus_rankup || 0,
      activeness_level: row?.activeness_level || 0,
      bonus_activeness: row?.bonus_activeness || 0,
      is_active: row?.is_active,
      manager_name: row?.manager_name,
    }));
  }, [allData]);

  // Filter
  const filtered = useMemo(() => {
    let list = creatorAggregates;
    if (selectedServer !== 'Tous') list = list?.filter(c => c?.server === selectedServer);
    if (levelFilter) list = list?.filter(c => c?.level === levelFilter);
    if (search) list = list?.filter(c => c?.username?.toLowerCase()?.includes(search?.toLowerCase()) || c?.tiktok_id?.includes(search));
    return list;
  }, [creatorAggregates, selectedServer, levelFilter, search]);

  const sorted = useMemo(() => {
    return [...filtered]?.sort((a, b) => {
      const aVal = a?.[sortKey];
      const bVal = b?.[sortKey];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'string') return sortDir === 'asc' ? aVal?.localeCompare(bVal) : bVal?.localeCompare(aVal);
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted?.length / PAGE_SIZE));
  const paginated = sorted?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
    setPage(1);
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <span style={{ color: 'rgba(255,255,255,0.15)' }}>↕</span>;
    return <span style={{ color: '#E5202A' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  // Server diamonds
  const serverDiamonds = useMemo(() => {
    const sd = {};
    allData?.forEach(r => { if (!sd?.[r?.server]) sd[r.server] = 0; sd[r.server] += r?.diamonds || 0; });
    return sd;
  }, [allData]);

  const joursInjectes = useMemo(() => allData?.[0]?.days_elapsed || 1, [allData]);

  // Activeness/rankup stats
  const activenessDistribution = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  creatorAggregates?.forEach(c => { activenessDistribution[c.activeness_level || 0]++; });
  const totalBonusActiveness = creatorAggregates?.reduce((s, c) => s + (c?.bonus_activeness || 0), 0);
  const rankingUp = creatorAggregates?.filter(c => c?.tier_current > c?.tier_m1 && (c?.bonus_rankup || 0) > 0)?.length;
  const maintaining = creatorAggregates?.filter(c => c?.tier_current === c?.tier_m1 && c?.tier_m1 >= 2 && (c?.bonus_rankup || 0) > 0)?.length;
  const tierLost = creatorAggregates?.filter(c => c?.tier_current < c?.tier_m1)?.length;
  const totalBonusRankup = creatorAggregates?.reduce((s, c) => s + (c?.bonus_rankup || 0), 0);

  // Bonus Net Estimé using manager formula
  const totalBonusNet = useMemo(() => {
    const creators = selectedServer === 'Tous' ? creatorAggregates : creatorAggregates?.filter(c => c?.server === selectedServer);
    return creators?.reduce((sum, c) => {
      const m = monthlyBonusMap?.[c?.tiktok_id];
      return sum + calculateManagerBonus(m?.total_bonus || 0, m?.charge || 0, m?.incremental_bonus || 0, incrementalRate);
    }, 0);
  }, [creatorAggregates, monthlyBonusMap, incrementalRate, selectedServer]);

  const managerServers = [...new Set(creatorAggregates.map(c => c.server).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8 space-y-5 md:space-y-8">
      {/* Header */}
      <div>
        <h1 className="font-bold text-white mb-1" style={{ fontFamily: 'Sora, sans-serif', fontSize: 'clamp(18px, 4vw, 24px)' }}>{t('performance.title')}</h1>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif', fontSize: '13px' }}>
          {t(`months.${now?.getMonth()}`)} {now?.getFullYear()} — {t('performance.realtimeData')}
        </p>
      </div>
      {/* Server tabs — scrollable on mobile */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {SERVERS?.map(s => (
          <button
            key={s}
            onClick={() => setSelectedServer(s)}
            className="flex-shrink-0 px-4 py-2 rounded-lg text-sm font-bold transition-all"
            style={{
              backgroundColor: selectedServer === s ? (SERVER_CONFIG?.[s]?.color || '#E5202A') : '#111111',
              color: selectedServer === s ? 'white' : 'rgba(255,255,255,0.4)',
              border: `1px solid ${selectedServer === s ? (SERVER_CONFIG?.[s]?.color || '#E5202A') : '#1f1f1f'}`,
              fontFamily: 'Sora, sans-serif',
              minHeight: '44px',
            }}
          >
            {s === 'Tous' ? t('common.all') : s}
          </button>
        ))}
        <select
          value={levelFilter}
          onChange={e => setLevelFilter(e?.target?.value)}
          className="flex-shrink-0 px-3 py-2 rounded-lg text-base text-white border outline-none ml-auto"
          style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', fontFamily: 'Inter, sans-serif', minHeight: '44px' }}
        >
          <option value="">{t('common.allLevels')}</option>
          {['Junior', 'Rising', 'Senior', 'Elite']?.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>
      {/* Manager objectives per server — 1 column on mobile */}
      {managerServers?.length > 0 && (
        <div>
          <h2 className="text-xs font-bold text-white/60 uppercase tracking-widest mb-3" style={{ fontFamily: 'Sora, sans-serif' }}>
            {t('performance.objectives', { name: managerProfile?.name })}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {managerServers?.filter(s => selectedServer === 'Tous' || s === selectedServer)?.map(server => {
              const objKey = `objective_${server?.toLowerCase()}`;
              const objective = managerProfile?.[objKey] || 0;
              const diamonds = serverDiamonds?.[server] || 0;
              const pct = objective > 0 ? (diamonds / objective) * 100 : 0;
              const barColor = getProgressColor(pct);
              return (
                <div key={server} className="rounded-xl border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', padding: 'clamp(12px, 3vw, 20px)' }}>
                  <div className="flex items-center justify-between mb-3">
                    <ServerBadge server={server} />
                    <span className="text-xs font-bold" style={{ color: barColor, fontFamily: 'Sora, sans-serif' }}>{pct?.toFixed(1)}%</span>
                  </div>
                  <div className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Sora, sans-serif' }}>{fmtM(diamonds)}</div>
                  <div className="text-xs text-white/40 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>{t('performance.objective')} {fmtM(objective)}</div>
                  <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#1f1f1f' }}>
                    <div className="h-full rounded-full" style={{ width: `${Math.min((pct / 130) * 100, 100)}%`, backgroundColor: barColor }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* KPI Summary — 2x2 on mobile */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {[
          { label: t('performance.totalCreators'), value: filtered?.length },
          { label: t('performance.activeMonth'), value: filtered?.filter(c => c?.total_diamonds > 0)?.length, color: '#22c55e' },
          { label: t('performance.totalDiamonds'), value: fmtM(filtered?.reduce((s, c) => s + c?.total_diamonds, 0)), color: '#f97316' },
          { label: t('performance.daysInjected'), value: joursInjectes },
        ]?.map(card => (
          <div key={card?.label} className="rounded-xl border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', padding: 'clamp(12px, 3vw, 16px)' }}>
            <p className="text-xs text-white/40 mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>{card?.label}</p>
            <p className="text-2xl font-bold" style={{ color: card?.color || 'white', fontFamily: 'Sora, sans-serif' }}>{card?.value}</p>
          </div>
        ))}
      </div>
      {/* Objectifs à Date — stack vertically on mobile */}
      <div>
        <h2 className="text-xs font-bold text-white/60 uppercase tracking-widest mb-3" style={{ fontFamily: 'Sora, sans-serif' }}>{t('performance.objectivesDate')}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
          <ActivenessDistributionCard
            distribution={activenessDistribution}
            totalBonus={totalBonusActiveness}
            title="Activeness"
          />
          <div className="rounded-xl border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', padding: 'clamp(12px, 3vw, 20px)' }}>
            <p className="text-xs font-bold text-white/60 uppercase tracking-widest mb-3" style={{ fontFamily: 'Sora, sans-serif' }}>{t('performance.rankup')}</p>
            <div className="space-y-2">
              {[{ l: t('performance.rankingUp'), v: rankingUp, c: '#22c55e' }, { l: t('performance.maintaining'), v: maintaining, c: '#3b82f6' }]?.map(item => (
                <div key={item?.l} className="flex items-center justify-between p-2 rounded" style={{ backgroundColor: '#0d0d0d' }}>
                  <span className="text-xs" style={{ color: item?.c, fontFamily: 'Inter, sans-serif' }}>{item?.l}</span>
                  <span className="text-sm font-bold" style={{ color: item?.c, fontFamily: 'Sora, sans-serif' }}>{item?.v}</span>
                </div>
              ))}
            </div>
            <div className="pt-3 mt-3 border-t" style={{ borderColor: '#1f1f1f' }}>
              <p className="text-xs text-white/40" style={{ fontFamily: 'Inter, sans-serif' }}>{t('performance.totalBonusRankup')}</p>
              <p className="text-lg font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{fmtUSD(totalBonusRankup)}</p>
            </div>
          </div>
          <div className="rounded-xl border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', padding: 'clamp(12px, 3vw, 20px)' }}>
            <p className="text-xs font-bold text-white/60 uppercase tracking-widest mb-3" style={{ fontFamily: 'Sora, sans-serif' }}>{t('performance.totalBonus')}</p>
            <div className="space-y-2">
              <div className="flex justify-between"><span className="text-xs text-white/50" style={{ fontFamily: 'Inter, sans-serif' }}>{t('dashboard.activeness')}</span><span className="text-sm font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{fmtUSD(totalBonusActiveness)}</span></div>
              <div className="flex justify-between"><span className="text-xs text-white/50" style={{ fontFamily: 'Inter, sans-serif' }}>{t('dashboard.rankup')}</span><span className="text-sm font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{fmtUSD(totalBonusRankup)}</span></div>
              <div className="h-px" style={{ backgroundColor: '#1f1f1f' }} />
              <div className="flex justify-between items-center">
                <span className="text-sm font-bold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>{t('dashboard.total')}</span>
                <span className="text-xl font-bold" style={{ color: '#E5202A', fontFamily: 'Sora, sans-serif' }}>{fmtUSD(totalBonusActiveness + totalBonusRankup)}</span>
              </div>
              <div className="h-px" style={{ backgroundColor: '#1f1f1f' }} />
              <div className="flex justify-between items-center">
                <span className="text-sm font-bold" style={{ color: '#22c55e', fontFamily: 'Inter, sans-serif' }}>{t('dashboard.bonusNet')}</span>
                <span className="text-xl font-bold" style={{ color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>{fmtUSD(totalBonusNet)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Opportunités Bonus */}
      <div className="mb-6">
        <h2 className="text-xs font-bold text-white/60 uppercase tracking-widest mb-3" style={{ fontFamily: 'Sora, sans-serif' }}>{t('performancePage.opportunitiesBonus')}</h2>
        <OpportunitesBonusSection
          data={(allData || []).filter(r => selectedServer === 'Tous' || r?.server === selectedServer)}
          loading={loading}
          serverColor={SERVER_CONFIG?.[selectedServer]?.color || '#E5202A'}
          basePath="/manager/creators"
        />
      </div>

      {/* Creator Table — horizontal scroll with sticky username column */}
      <div>
        <div className="flex items-center justify-between mb-3 gap-3">
          <h2 className="text-xs font-bold text-white/60 uppercase tracking-widest" style={{ fontFamily: 'Sora, sans-serif' }}>{t('nav.creators')}</h2>
          <input
            type="text"
            placeholder={t('performance.searchCreator')}
            value={search}
            onChange={e => { setSearch(e?.target?.value); setPage(1); }}
            className="px-3 py-2 rounded-lg text-base text-white placeholder-white/30 border outline-none flex-1 md:flex-none"
            style={{ backgroundColor: '#111111', borderColor: '#1f1f1f', fontFamily: 'Inter, sans-serif', maxWidth: '220px', minHeight: '44px', fontSize: '16px' }}
          />
        </div>
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#1f1f1f' }}>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="w-full" style={{ minWidth: '500px' }}>
              <thead>
                <tr style={{ backgroundColor: '#0d0d0d', borderBottom: '1px solid #1f1f1f' }}>
                  {[
                    { key: 'username', label: t('creators.creator'), width: '140px', sticky: true },
                    { key: 'server', label: t('performancePage.server'), width: '70px', sortable: false },
                    { key: 'total_diamonds', label: t('performancePage.diamonds'), width: '100px' },
                    { key: 'live_days', label: t('creatorDetail.liveDays'), width: '80px' },
                    { key: 'tier_current', label: t('history.tier'), width: '60px' },
                    { key: 'activeness_level', label: t('performance.activeness'), width: '90px' },
                    { key: 'bonus_activeness', label: t('performancePage.bonusActiv'), width: '100px' },
                    { key: 'bonus_rankup', label: t('performancePage.bonusRankup'), width: '110px' },
                    { key: 'bonus_total', label: t('dashboard.total'), width: '100px' },
                  ]?.map(col => (
                    <th
                      key={col?.key}
                      onClick={() => col?.sortable !== false && handleSort(col?.key)}
                      className="px-4 py-3 text-left text-xs font-bold text-white/40 uppercase tracking-wider whitespace-nowrap select-none"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        width: col?.width,
                        cursor: col?.sortable !== false ? 'pointer' : 'default',
                        ...(col?.sticky ? { position: 'sticky', left: 0, backgroundColor: '#0d0d0d', zIndex: 1 } : {}),
                      }}
                    >
                      {col?.label} {col?.sortable !== false && <SortIcon col={col?.key} />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated?.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-white/30 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{t('performance.noCreators')}</td></tr>
                ) : paginated?.map(c => {
                  const bonusTotal = (c?.bonus_activeness || 0) + (c?.bonus_rankup || 0);
                  return (
                    <tr
                      key={c?.tiktok_id}
                      onClick={() => navigate(`/manager/creators/${c?.tiktok_id}`)}
                      className="cursor-pointer transition-colors"
                      style={{ borderBottom: '1px solid #1f1f1f' }}
                      onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#161616'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <td className="px-4 py-3" style={{ maxWidth: '140px', overflow: 'hidden', position: 'sticky', left: 0, backgroundColor: 'inherit', zIndex: 1 }}>
                        <span className="text-sm text-white" style={{ fontFamily: 'Inter, sans-serif', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c?.username}</span>
                      </td>
                      <td className="px-4 py-3" style={{ width: '70px' }}><ServerBadge server={c?.server} /></td>
                      <td className="px-4 py-3" style={{ width: '100px' }}><span className="text-sm font-bold" style={{ color: '#f97316', fontFamily: 'Sora, sans-serif' }}>{fmtM(c?.total_diamonds)}</span></td>
                      <td className="px-4 py-3" style={{ width: '80px' }}><span className="text-sm text-white/60" style={{ fontFamily: 'Inter, sans-serif' }}>{c?.live_days}</span></td>
                      <td className="px-4 py-3" style={{ width: '60px' }}>
                        <span className="text-xs font-bold px-2 py-0.5 rounded-md" style={{ backgroundColor: 'rgba(229,32,42,0.12)', color: '#E5202A', fontFamily: 'Sora, sans-serif' }}>T{c?.tier_current || 1}</span>
                      </td>
                      <td className="px-4 py-3" style={{ width: '90px' }}><span className="text-sm text-white/60" style={{ fontFamily: 'Inter, sans-serif' }}>L{c?.activeness_level || 0}</span></td>
                      <td className="px-4 py-3" style={{ width: '100px' }}><span className="text-sm text-white/60" style={{ fontFamily: 'Inter, sans-serif' }}>{fmtUSD(c?.bonus_activeness)}</span></td>
                      <td className="px-4 py-3" style={{ width: '110px' }}><span className="text-sm text-white/60" style={{ fontFamily: 'Inter, sans-serif' }}>{fmtUSD(c?.bonus_rankup)}</span></td>
                      <td className="px-4 py-3" style={{ width: '100px' }}>
                        <span className="text-sm font-bold" style={{ color: bonusTotal > 0 ? '#E5202A' : 'rgba(255,255,255,0.2)', fontFamily: 'Sora, sans-serif' }}>{bonusTotal > 0 ? fmtUSD(bonusTotal) : '—'}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: '#1f1f1f' }}>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
              {t('performancePage.creatorCount', { count: sorted?.length })} · Page {page}/{totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-2 rounded text-xs font-medium transition-all disabled:opacity-30"
                style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.6)', fontFamily: 'Inter, sans-serif', border: '1px solid #2a2a2a', minHeight: '44px', minWidth: '44px' }}
              >
                {t('common.prev')}
              </button>
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-2 rounded text-xs font-medium transition-all disabled:opacity-30"
                style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.6)', fontFamily: 'Inter, sans-serif', border: '1px solid #2a2a2a', minHeight: '44px', minWidth: '44px' }}
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerPerformance;
